import { useState } from "react";
import { motion } from "framer-motion";
import { Play, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";

export default function VideoGallery({ data }) {
  const [openIdx, setOpenIdx] = useState(null);
  const videos = data.videos || [];

  return (
    <section data-testid="video-gallery-section" className="py-24 lg:py-32 bg-onyx text-alabaster">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="max-w-3xl mb-12">
          <p className="overline mb-4 text-gold" data-testid="video-gallery-overline">{data.overline}</p>
          <h2
            data-testid="video-gallery-title"
            className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light"
          >
            {data.title}
          </h2>
          <p className="mt-4 text-alabaster/70">{data.subtitle}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {videos.map((v, i) => (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              onClick={() => setOpenIdx(i)}
              data-testid={`video-${i}`}
              className="group relative overflow-hidden aspect-video text-left"
            >
              <img
                src={v.thumbnail || "https://images.unsplash.com/photo-1518310383802-640c2de681b3?w=800&q=80"}
                alt={v.title}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-onyx/40 group-hover:bg-onyx/30 transition-colors" />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="w-16 h-16 rounded-full bg-gold flex items-center justify-center group-hover:scale-110 transition-transform">
                  <Play size={24} className="text-onyx ml-1" fill="currentColor" />
                </span>
              </div>
              <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-onyx to-transparent">
                <p className="text-sm font-semibold tracking-wide">{v.title}</p>
              </div>
            </motion.button>
          ))}
        </div>

        <Dialog open={openIdx !== null} onOpenChange={(o) => !o && setOpenIdx(null)}>
          <DialogContent
            data-testid="video-dialog"
            className="max-w-4xl bg-onyx border-onyx p-0 overflow-hidden"
          >
            <DialogTitle className="sr-only">
              {openIdx !== null ? videos[openIdx]?.title : "Video"}
            </DialogTitle>
            <button
              data-testid="video-dialog-close"
              onClick={() => setOpenIdx(null)}
              className="absolute right-3 top-3 z-10 w-9 h-9 rounded-full bg-onyx/80 text-alabaster flex items-center justify-center hover:bg-gold hover:text-onyx transition-colors"
            >
              <X size={18} />
            </button>
            {openIdx !== null && (
              <div className="aspect-video w-full">
                <iframe
                  src={videos[openIdx].url}
                  title={videos[openIdx].title}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </section>
  );
}
